import { useState, useEffect, useCallback } from 'react';

export interface UserLocation {
  lat: number;
  lng: number;
  label: string;
  state?: string;
  source: 'gps' | 'zip' | 'manual';
}

const STORAGE_KEY = 'ahp_user_location';

function loadFromStorage(): UserLocation | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return null;
}

function saveToStorage(location: UserLocation | null) {
  try {
    if (location) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(location));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {}
}

// ── Shared store ─────────────────────────────────────────────────────────────
// Location is app-global state: multiple components mount this hook at once
// (page header + a section modal, etc.). Before this store each instance kept
// its own useState snapshot — setting the location inside the Nearby-Hospitals
// modal updated that instance only, and the page behind it never learned.
// All instances now subscribe to one module-level value.
let currentLocation: UserLocation | null = loadFromStorage();
const locationListeners = new Set<(loc: UserLocation | null) => void>();

function publishLocation(loc: UserLocation | null) {
  currentLocation = loc;
  saveToStorage(loc);
  locationListeners.forEach((fn) => fn(loc));
}

export function useUserLocation(autoDetect = false) {
  const [location, setLocationState] = useState<UserLocation | null>(currentLocation);
  const [isLocating, setIsLocating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoDetectAttempted, setAutoDetectAttempted] = useState(false);

  useEffect(() => {
    locationListeners.add(setLocationState);
    return () => { locationListeners.delete(setLocationState); };
  }, []);

  const setLocation = useCallback((loc: UserLocation | null) => {
    publishLocation(loc);
    setError(null);
  }, []);

  const detectLocation = useCallback(async () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return;
    }

    setIsLocating(true);
    setError(null);

    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 300000,
        });
      });

      const { latitude, longitude } = position.coords;

      // Reverse geocode with Nominatim
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&addressdetails=1`,
        { headers: { 'User-Agent': 'AmericanHealthPrices/1.0' } }
      );

      if (!response.ok) throw new Error('Geocoding failed');
      const data = await response.json();

      const city = data.address?.city || data.address?.town || data.address?.village || '';
      const state = data.address?.state || '';
      const stateCode = getStateCode(state);

      setLocation({
        lat: latitude,
        lng: longitude,
        label: city && stateCode ? `${city}, ${stateCode}` : data.display_name?.split(',').slice(0, 2).join(',') || 'Your Location',
        state: stateCode || undefined,
        source: 'gps',
      });
    } catch (err: any) {
      if (err.code === 1) {
        setError('Location access denied. Please allow location access or enter a ZIP code.');
      } else {
        setError('Could not determine your location. Try entering a ZIP code.');
      }
    } finally {
      setIsLocating(false);
    }
  }, [setLocation]);

  // Auto-detect GPS on mount if no stored location and autoDetect is enabled
  useEffect(() => {
    if (autoDetect && !location && !autoDetectAttempted && !isLocating) {
      setAutoDetectAttempted(true);
      detectLocation();
    }
  }, [autoDetect, location, autoDetectAttempted, isLocating, detectLocation]);

  const lookupZip = useCallback(async (zip: string) => {
    if (!/^\d{5}$/.test(zip)) return;

    setIsLocating(true);
    setError(null);

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?postalcode=${zip}&country=US&format=json&addressdetails=1&limit=1`,
        { headers: { 'User-Agent': 'AmericanHealthPrices/1.0' } }
      );

      if (!response.ok) throw new Error('Geocoding failed');
      const results = await response.json();

      if (results.length === 0) {
        setError('ZIP code not found');
        setIsLocating(false);
        return;
      }

      const result = results[0];
      const lat = parseFloat(result.lat);
      const lng = parseFloat(result.lon);
      const stateCode = getStateCode(result.address?.state || '');
      const city = result.address?.city || result.address?.town || result.address?.village || '';

      setLocation({
        lat,
        lng,
        label: city && stateCode ? `${city}, ${stateCode}` : `ZIP ${zip}`,
        state: stateCode || undefined,
        source: 'zip',
      });
    } catch {
      setError('Could not look up ZIP code. Please try again.');
    } finally {
      setIsLocating(false);
    }
  }, [setLocation]);

  const clearLocation = useCallback(() => {
    setLocation(null);
  }, [setLocation]);

  return { location, isLocating, error, detectLocation, lookupZip, clearLocation, setLocation };
}

// Map full state names to two-letter codes
const STATE_MAP: Record<string, string> = {
  'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR', 'california': 'CA',
  'colorado': 'CO', 'connecticut': 'CT', 'delaware': 'DE', 'florida': 'FL', 'georgia': 'GA',
  'hawaii': 'HI', 'idaho': 'ID', 'illinois': 'IL', 'indiana': 'IN', 'iowa': 'IA',
  'kansas': 'KS', 'kentucky': 'KY', 'louisiana': 'LA', 'maine': 'ME', 'maryland': 'MD',
  'massachusetts': 'MA', 'michigan': 'MI', 'minnesota': 'MN', 'mississippi': 'MS', 'missouri': 'MO',
  'montana': 'MT', 'nebraska': 'NE', 'nevada': 'NV', 'new hampshire': 'NH', 'new jersey': 'NJ',
  'new mexico': 'NM', 'new york': 'NY', 'north carolina': 'NC', 'north dakota': 'ND', 'ohio': 'OH',
  'oklahoma': 'OK', 'oregon': 'OR', 'pennsylvania': 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
  'south dakota': 'SD', 'tennessee': 'TN', 'texas': 'TX', 'utah': 'UT', 'vermont': 'VT',
  'virginia': 'VA', 'washington': 'WA', 'west virginia': 'WV', 'wisconsin': 'WI', 'wyoming': 'WY',
  'district of columbia': 'DC',
};

function getStateCode(stateName: string): string | null {
  if (!stateName) return null;
  // Already a code
  if (/^[A-Z]{2}$/.test(stateName)) return stateName;
  return STATE_MAP[stateName.toLowerCase()] || null;
}
